import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { Controller } from "react-hook-form";
import type {
  Control,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";
import type { RefCallback } from "react";
import type { TFunction } from "i18next";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DropDown from "@/components/forms/inputs/dropDown";
import type {
  LmsCourseEditorFormValues,
  LmsCourseSharedSettingsFormValues,
  LmsDropDownOption,
} from "@/components/lms/lms.utils";
import styles from "@/routes/lms/lmsCourseOverview.module.css";
import { isValidLmsIntervalValue } from "@/components/lms/lms.utils";

interface Props {
  categoryOptions: LmsDropDownOption[];
  certificateExpirable: boolean;
  control: Control<LmsCourseEditorFormValues>;
  disabled: boolean;
  errors: FieldErrors<LmsCourseEditorFormValues>;
  getFieldErrorMessage: (
    fieldName: keyof LmsCourseSharedSettingsFormValues
  ) => string;
  registerValidationTarget: (
    fieldName: SharedValidationField
  ) => RefCallback<HTMLElement>;
  t: TFunction;
}

export type SharedValidationField =
  | "category"
  | "completionIntervalInDays"
  | "renewalCooldownInDays"
  | "validityIntervalInDays";

export default function LmsCourseSharedSettingsForm({
  categoryOptions,
  certificateExpirable,
  control,
  disabled,
  errors,
  getFieldErrorMessage,
  registerValidationTarget,
  t,
}: Props) {
  return (
    <section className={styles.settingsPanel}>
      <div className={styles.settingsSectionHeader}>
        <h2>{t("course_settings")}</h2>
      </div>

      <div className={styles.settingsGrid}>
        <div
          className={styles.fieldBlock}
          ref={registerValidationTarget("category")}
        >
          <Controller
            name="category"
            control={control}
            rules={{ required: t("field_required") }}
            render={({ field }) => (
              <>
                <div className={styles.fieldLabel}>
                  {t("category")}
                  <span className={styles.requiredAsterisk}>*</span>
                </div>
                <DropDown
                  options={categoryOptions}
                  onChange={field.onChange}
                  externalValue={field.value}
                  disabled={disabled}
                  name="course-overview-category"
                />
                {getFieldErrorMessage("category") && (
                  <div className={styles.fieldErrorText}>
                    {getFieldErrorMessage("category")}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <BooleanToggleField
          control={control}
          disabled={disabled}
          fieldName="showCourseDurationOnCertificate"
          label={t("show_course_duration_on_certificate")}
          t={t}
        />
        <BooleanToggleField
          control={control}
          disabled={disabled}
          fieldName="isPrivate"
          label={t("private_course")}
          t={t}
        />
        <BooleanToggleField
          control={control}
          disabled={disabled}
          fieldName="certificateExpirable"
          label={t("course_certificate_expirable")}
          t={t}
        />
        <p>{t("course_recurrence_help")}</p>

        <div className={styles.fieldBlock}>
          <Controller
            name="completionIntervalInDays"
            control={control}
            rules={{
              required: t("field_required"),
              validate: (value) =>
                Number(value) > 0 || t("field_must_be_greater_than_zero"),
            }}
            render={({ field }) => (
              <DayCountField
                field={field}
                disabled={disabled}
                errorMessage={errors.completionIntervalInDays?.message}
                helpText={t("completion_interval_in_days_help")}
                label={t("completion_interval_in_days")}
                min={1}
                validationTargetRef={registerValidationTarget(
                  "completionIntervalInDays"
                )}
              />
            )}
          />
        </div>

        {certificateExpirable && (
          <div className={styles.fieldBlock}>
            <Controller
              name="renewalCooldownInDays"
              control={control}
              rules={{
                validate: (value) =>
                  (Boolean(value.trim()) && isValidLmsIntervalValue(value)) ||
                  t("field_must_be_zero_or_greater"),
              }}
              render={({ field }) => (
                <DayCountField
                  field={field}
                  disabled={disabled}
                  errorMessage={errors.renewalCooldownInDays?.message}
                  helpText={t("renewal_cooldown_in_days_help")}
                  label={t("renewal_cooldown_in_days")}
                  min={0}
                  validationTargetRef={registerValidationTarget(
                    "renewalCooldownInDays"
                  )}
                />
              )}
            />
          </div>
        )}
        {certificateExpirable && (
          <div className={styles.fieldBlock}>
            <Controller
              name="validityIntervalInDays"
              control={control}
              rules={{
                validate: (value) =>
                  !certificateExpirable ||
                  (Boolean(value.trim()) && isValidLmsIntervalValue(value)) ||
                  t("field_must_be_zero_or_greater"),
              }}
              render={({ field }) => (
                <DayCountField
                  field={field}
                  disabled={disabled}
                  errorMessage={errors.validityIntervalInDays?.message}
                  helpText={t("course_validity_interval_help")}
                  label={t("validity_interval_in_days")}
                  min={0}
                  validationTargetRef={registerValidationTarget(
                    "validityIntervalInDays"
                  )}
                />
              )}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function DayCountField({
  disabled,
  errorMessage,
  field,
  helpText,
  label,
  min,
  validationTargetRef,
}: {
  disabled: boolean;
  errorMessage?: string;
  field: ControllerRenderProps<
    LmsCourseEditorFormValues,
    Exclude<SharedValidationField, "category">
  >;
  helpText: string;
  label: string;
  min: number;
  validationTargetRef: RefCallback<HTMLElement>;
}) {
  return (
    <>
      <div className={styles.fieldLabelWithTooltip}>
        {label}
        <span className={styles.requiredAsterisk}>*</span>
        <Tooltip title={helpText} placement="right" arrow>
          <GenericButton
            variant="icon"
            color="iconBase"
            size="small"
            className={styles.helpIconButton}
          >
            <HelpOutlineIcon fontSize="small" />
          </GenericButton>
        </Tooltip>
      </div>
      <div ref={validationTargetRef}>
        <DefaultInput {...field} type="number" min={min} disabled={disabled} />
      </div>
      {errorMessage && (
        <div className={styles.fieldErrorText}>{errorMessage}</div>
      )}
    </>
  );
}

function BooleanToggleField({
  control,
  disabled,
  errorMessage,
  fieldName,
  label,
  onValueChange,
  registerValidationTarget,
  t,
  validate,
}: {
  control: Control<LmsCourseEditorFormValues>;
  disabled: boolean;
  errorMessage?: string;
  fieldName:
    | "isActive"
    | "showCourseDurationOnCertificate"
    | "certificateExpirable"
    | "isPrivate";
  label: string;
  onValueChange?: (value: boolean) => void;
  registerValidationTarget?: RefCallback<HTMLElement>;
  t: TFunction;
  validate?: (value: boolean) => true | string;
}) {
  return (
    <div className={styles.toggleFieldRow} ref={registerValidationTarget}>
      <div className={styles.fieldLabel}>
        {label}
        <span className={styles.requiredAsterisk}>*</span>
      </div>
      <Controller
        name={fieldName}
        control={control}
        rules={validate ? { validate } : undefined}
        render={({ field }) => (
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="secondary"
            data-testid={`lms-course-${fieldName}-toggle`}
            disabled={disabled}
            value={field.value ? "yes" : "no"}
            onChange={(_event, value) => {
              if (!value) return;
              const nextValue = value === "yes";
              field.onChange(nextValue);
              onValueChange?.(nextValue);
            }}
            sx={{
              height: "56px",
              "& .MuiToggleButton-root": {
                height: "56px",
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="yes">{t("yes")}</ToggleButton>
            <ToggleButton value="no">{t("no")}</ToggleButton>
          </ToggleButtonGroup>
        )}
      />
      {errorMessage && (
        <div className={styles.fieldErrorText}>{errorMessage}</div>
      )}
    </div>
  );
}
